/**
 * Offline pre-render of the SFX recipes (audio-direction §7, approach A): each variation of a sound is one
 * `OfflineAudioContext` render of its layers with a seed derived from (render seed, sound, variation). Then:
 * loops fold their tail over their head (equal-power crossfade, seamless), one-shots get a short tail fade,
 * and all variations of a sound share one peak normalisation (to `normalizeDb`), so `mix.gain` is a clean
 * relative level. Deterministic: same config + seed → the same buffers.
 */
import { createRng } from '../../core/rng'
import type { RecipeDef, SoundDef } from '../sfxSchema'
import { bufferPeak, dbToGain } from './dsp'
import { buildLayer, type Layer } from './layers'

/** The part of `OfflineAudioContext` a render needs (a real one, or the test fake). */
export type OfflineContextLike = BaseAudioContext & { startRendering(): Promise<AudioBuffer> }

/** Creates an offline context: `new OfflineAudioContext(channels, length, sampleRate)` in the browser. */
export type OfflineFactory = (channels: number, length: number, sampleRate: number) => OfflineContextLike

/** The browser's factory, or null where `OfflineAudioContext` does not exist. */
export function browserOfflineFactory(): OfflineFactory | null {
  if (typeof OfflineAudioContext === 'undefined') return null
  return (channels, length, sampleRate) => new OfflineAudioContext(channels, length, sampleRate)
}

/** A 32-bit seed mixing the render seed, the sound index and the variation (splitmix-style). */
export function variantSeed(seed: number, sound: number, variant: number): number {
  let h = (seed ^ Math.imul(sound + 1, 0x9e3779b1) ^ Math.imul(variant + 1, 0x85ebca77)) >>> 0
  h = Math.imul(h ^ (h >>> 16), 0x7feb352d) >>> 0
  h = Math.imul(h ^ (h >>> 15), 0x846ca68b) >>> 0
  return (h ^ (h >>> 16)) >>> 0
}

/** Samples rendered for `def` (loops add their crossfade). */
export function renderLength(def: Pick<RecipeDef, 'seconds' | 'loop'>, sampleRate: number): number {
  return Math.ceil((def.seconds + (def.loop?.crossfade ?? 0)) * sampleRate)
}

/** Builds the graph of `layers` into `ctx` (the pure recipe: seed → graph). */
export function buildRecipe(ctx: BaseAudioContext, layers: readonly Layer[], channels: 1 | 2, seconds: number, seed: number, sustain: boolean): void {
  const out = ctx.createGain()
  out.connect(ctx.destination)
  const target = { ac: ctx, out, channels, rng: createRng(seed), seconds }
  for (const layer of layers) buildLayer(target, layer, sustain)
}

/** Renders one variation (raw: no fold, fade or normalisation). */
export async function renderRaw(factory: OfflineFactory, def: RecipeDef, sampleRate: number, seed: number): Promise<AudioBuffer> {
  const length = renderLength(def, sampleRate)
  const ctx = factory(def.channels, length, sampleRate)
  buildRecipe(ctx, def.layers, def.channels, length / sampleRate, seed, def.loop !== undefined)
  return ctx.startRendering()
}

/**
 * Folds the last `crossfade` seconds of a loop render over its head (equal power), so the buffer loops without
 * a seam. Returns a new buffer of `seconds` length created with `create`.
 */
export function foldLoop(src: AudioBuffer, seconds: number, create: (channels: number, length: number, rate: number) => AudioBuffer): AudioBuffer {
  const rate = src.sampleRate
  const n = Math.min(Math.round(seconds * rate), src.length)
  const x = src.length - n
  const out = create(src.numberOfChannels, n, rate)
  for (let c = 0; c < src.numberOfChannels; c++) {
    const a = src.getChannelData(c)
    const o = out.getChannelData(c)
    o.set(a.subarray(0, n))
    for (let i = 0; i < x && i < n; i++) {
      const u = (i / x) * (Math.PI / 2)
      o[i] = a[i] * Math.sin(u) + a[n + i] * Math.cos(u)
    }
  }
  return out
}

/** Linear fade over the last `seconds` of every channel (in place). */
export function fadeTail(buffer: AudioBuffer, seconds: number): void {
  const k = Math.min(buffer.length, Math.ceil(seconds * buffer.sampleRate))
  if (k <= 0) return
  for (let c = 0; c < buffer.numberOfChannels; c++) {
    const d = buffer.getChannelData(c)
    for (let i = 0; i < k; i++) d[d.length - k + i] *= 1 - (i + 1) / k
  }
}

/** Scales every buffer by one factor so the loudest peak lands at `db` dBFS (silent sets stay silent). */
export function normalizeSet(buffers: readonly AudioBuffer[], db: number): number {
  let peak = 0
  for (const b of buffers) peak = Math.max(peak, bufferPeak(b))
  if (!(peak > 0)) return 1
  const k = dbToGain(db) / peak
  for (const b of buffers) {
    for (let c = 0; c < b.numberOfChannels; c++) {
      const d = b.getChannelData(c)
      for (let i = 0; i < d.length; i++) d[i] *= k
    }
  }
  return k
}

export interface RenderedSound {
  readonly takes: AudioBuffer[]
  /** Normalisation factor applied. */
  readonly gain: number
}

/**
 * Renders every variation of `def` (`mix.variants`) concurrently, then post-processes them as a set.
 * `create` makes plain buffers for loop folding (the live or offline context's `createBuffer`).
 */
export async function renderSound(
  factory: OfflineFactory,
  def: SoundDef,
  sound: number,
  seed: number,
  sampleRate: number,
  tailFade: number,
  create: (channels: number, length: number, rate: number) => AudioBuffer,
): Promise<RenderedSound> {
  const jobs: Promise<AudioBuffer>[] = []
  for (let v = 0; v < def.mix.variants; v++) jobs.push(renderRaw(factory, def, sampleRate, variantSeed(seed, sound, v)))
  const raw = await Promise.all(jobs)
  const takes = raw.map((b) => {
    if (def.loop !== undefined) return foldLoop(b, def.seconds, create)
    fadeTail(b, tailFade)
    return b
  })
  const gain = normalizeSet(takes, def.normalizeDb)
  return { takes, gain }
}

/** Renders one recipe (a single take: folded if it loops, tail-faded otherwise, normalised). */
export async function renderRecipe(
  factory: OfflineFactory,
  def: RecipeDef,
  seed: number,
  sampleRate: number,
  create: (channels: number, length: number, rate: number) => AudioBuffer,
): Promise<AudioBuffer> {
  const raw = await renderRaw(factory, def, sampleRate, seed)
  const take = def.loop !== undefined ? foldLoop(raw, def.seconds, create) : raw
  if (def.loop === undefined) fadeTail(take, 0.005)
  normalizeSet([take], def.normalizeDb)
  return take
}
